import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";

// Contexts & Variables
import { AuditContext } from "../../../../contexts/Audit";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import { BASE_URL } from "../../../../BaseUrl";
import { getCookie, _t, customStyle } from "../../../../functions/Functions";

// Components
import AuditSidebar from "./AuditSidebar";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import { ToastSuccess, ToastError } from "../../../../functions/ToastShow";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const RcrAuditAction = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const { branchForListSearch, branchForSearch } = useContext(RestaurantContext);
  const { rcrSections, getRcrSections, loading } = useContext(AuditContext);

  const [fetchingData, setFetchingData] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    branch_id: "",
    section: null,
    audit_date: new Date(),
  });
  const [answers, setAnswers] = useState([]);

  useEffect(() => {
    if (!rcrSections) getRcrSections();
  }, []);

  useEffect(()=>{
    if(id && rcrSections){
      fetchAuditDetails(id, rcrSections[0]);
    }
  },[rcrSections, id])

  const fetchAuditDetails = async (auditId, section) => {
    setFetchingData(true);
    try {
      const url = BASE_URL + `/settings/edit-audit-section`;
      const res = await axios.get(url, {
        params:{
          audit_id: auditId,
          section_id: section.id,
        },
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      const data = res.data;
      setFormData({
        branch_id: data.branch.id || "",
        section: section,
        audit_date: data.audit.audit_date ? new Date(data.audit.audit_date) : new Date(),
      });
      setFetchingData(false);
      if(!data?.section || !data.answers?.length){
        ToastError({ title: _t(t("No audit found for this specific section and branch")) });
        return
      }
      if (data?.answers) {
        setAnswers(data.answers.map(ans => ({
          ...ans,
          question: ans.question_name || ans?.title,
          max_marks: ans.max_marks || 0,
          critical: ans.is_critical == 1 ? 1 : 0,
          status: ans.status || "Non-Compliant",
          marks_obtained: ans?.marks_obtained || 0,
          is_repeat: ans?.is_repeat == 1 ? 1 : 0,
        })));
      }
    } catch (error) {
      setFetchingData(false);
      if(error.response.data.messsage){
        ToastError({ title: _t(t(error.response.data.messsage)) });
        return
      }
      ToastError({ title: _t(t("Failed to load audit details")) });
    }
  };

  const fetchQuestions = async (sectionId) => {
    setFetchingData(true);
    try {
      const url = BASE_URL + "/settings/questions";
      const res = await axios.get(url, {
        params: { section_id: sectionId },
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data.data) {
        const initAnswers = res.data.data.questions?.map(q => ({
          question_id: q.id,
          question: q.question_text,
          max_marks: q.max_marks || 0,
          critical: q.is_critical == 1 ? 1 : 0,
          status: q.status || "Non-Compliant",
          marks_obtained: q.marks_obtained || 0,
          is_repeat: q.is_repeat == 1 ? 1 : 0,
          evidence: q.evidence || "",
          remarks: q.remarks || ""
        }));
        setAnswers(initAnswers);
        setFormData(prev => ({ ...prev, section: res.data.data?.section }));
      } else {
        setAnswers([]);
      }
      setFetchingData(false);
    } catch (error) {
      setFetchingData(false);
      toast.error(_t(t("Failed to fetch questions")));
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData(prev => ({ 
      ...prev, 
      [name]: name === 'section' ? (selectedOption || null) : (selectedOption ? (selectedOption.id || selectedOption.value) : "") 
    }));
    if(selectedOption && name === 'section' && !id){
      fetchQuestions(selectedOption?.id)
    }else if(id && selectedOption){
      fetchAuditDetails(id, selectedOption)
    }
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAnswerChange = (index, field, value) => {
    const updatedAnswers = [...answers];
    updatedAnswers[index][field] = value;
    setAnswers(updatedAnswers);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.branch_id || !formData.section ||  !formData.audit_date) {
      ToastError({ title: _t(t("Please fill all required fields")) });
      return;
    }
    
    // Validate answers
    const invalidAnswer = answers.find(a => a.marks_obtained === "" || a.marks_obtained === null);
    if (invalidAnswer) {
      ToastError({ title: _t(t("Marks obtained is mandatory for all questions")) });
      return;
    }

    setSubmitting(true);
    const payload = {
      branch_id: formData.branch_id,
      section_id: formData.section?.id,
      scorecard: {
        total_applicable_marks: totalMaxMarks,
        total_obtained_marks: totalMarksObtained,
        section_weightage: formData.section.weightage,
        repeat_findings_count: repeatFindingsCount,
        repeat_penalty: repeatPenalty,
        section_score: sectionScore,
        critical_non_compliances_count: criticalNonCompliancesCount
      },
      audit_date: formData.audit_date,
      answers: answers
    };

    if(id){
      payload.audit_id= id
    }

    try {
      const url =`${BASE_URL}/settings/save-answers`;
      const method = 'post';
      await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      
      ToastSuccess({ title: _t(t(`Audit successfully ${id ? 'updated' : 'created'}`)) });
      history.push("/dashboard/audit/rcr-list");
    } catch (error) {
      ToastError({ title: _t(t("Failed to save audit")) });
      setSubmitting(false);
    }
  };

  const {
    totalMaxMarks,
    totalMarksObtained,
    repeatFindingsCount,
    repeatPenalty,
    criticalNonCompliancesCount,
    sectionScore
  } = useMemo(() => {
    let max = 0, obtained = 0, repeatCount = 0, criticalCount = 0;

    answers.forEach(ans => {
      if (ans.status !== 'N/A') {
        max += Number(ans.max_marks) || 0;
        obtained += Number(ans.marks_obtained) || 0;
      }
      if (ans.is_repeat == 1) {
        repeatCount++;
      }
      if (ans.critical == 1 && ans.status === 'Non-Compliant') {
        criticalCount++;
      }
    });

    let penalty = Math.min(repeatCount * 2, 10);
    let scoreRaw = max > 0 ? (obtained / max) * 100 : 0;
    let finalScore = isNaN(scoreRaw) ? 0 : Math.max(scoreRaw - penalty, 0);

    return {
      totalMaxMarks: max,
      totalMarksObtained: obtained,
      repeatFindingsCount: repeatCount,
      repeatPenalty: penalty,
      criticalNonCompliancesCount: criticalCount,
      sectionScore: finalScore.toFixed(1)
    };
  }, [answers]);

  return (
    <>
      <Helmet>
        <title>{id ? _t(t("Edit Audit")) : _t(t("Add Audit"))}</title>
      </Helmet>
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <AuditSidebar />
            </div>
            
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="fk-scroll--pos-menu table-bottom-info-hide" data-simplebar>
                  <div className="t-pl-15 t-pr-15">
                    <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                      <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                        <ul className="t-list fk-breadcrumb">
                          <li className="fk-breadcrumb__list">
                            <span className="t-link fk-breadcrumb__link text-capitalize">
                              {id ? _t(t("Edit Audit")) : _t(t("New Audit"))}
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    
                    <>
                      <CustomSpinLoader loading={loading || fetchingData}/>
                      <form onSubmit={handleSubmit}>
                        <div className="border border-secondary-light rounded px-3 py-2 mb-2 bg-white">
                          <div className="row mb-2">
                            <div className="col-md-4">
                              <div className="form-group">
                                <label className="font-weight-600 mb-1 fk-sm-card__title text-dark form-label">
                                  {_t(t("Branch"))} <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={branchForSearch || []}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.id}
                                  value={branchForListSearch && branchForListSearch[formData.branch_id] || null}
                                  onChange={(selected) => handleSelectChange('branch_id', selected)}
                                  placeholder={_t(t("Select Branch..."))}
                                  isDisabled={!!id}
                                  classNamePrefix="select table-text"
                                  styles={customStyle}
                                />
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <label className="font-weight-600 mb-1 fk-sm-card__title text-dark form-label">
                                  {_t(t("Section"))} <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={rcrSections || []}
                                  getOptionLabel={(option) => option.name || option.title || `Section ${option.id}`}
                                  getOptionValue={(option) => option.id}
                                  value={formData.section}
                                  onChange={(selected) => handleSelectChange('section', selected)}
                                  placeholder={_t(t("Select Section..."))}
                                  classNamePrefix="select table-text"
                                  styles={customStyle}
                                />
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="form-group">
                                <label className="font-weight-600 mb-1 fk-sm-card__title text-dark form-label">
                                  {_t(t("Audit Date"))} <span className="text-danger">*</span>
                                </label>
                                <div className="w-100">
                                  <DatePicker
                                    selected={formData.audit_date}
                                    onChange={(date) => handleDateChange('audit_date', date)}
                                    dateFormat="yyyy-MM-dd"
                                    className="form-control table-text"
                                    placeholderText={_t(t("Audit Date"))}
                                    disabled={!!id}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {answers && answers.length > 0 && (
                            <div className="table-responsive my-2">
                              <table className="table table-bordered mb-0">
                                <tbody>
                                  <tr>
                                    <td className="align-middle font-weight-bold">
                                      Outlet:
                                    </td>
                                    <td className="align-middle text-right">
                                      {formData.section?.wastage || 0}
                                    </td>
                                    <td className="align-middle font-weight-bold text-center" style={{ color: '#1c243a', width: '15%' }}>
                                      Section Weight
                                    </td>
                                    <td className="align-middle font-weight-bold text-center" style={{ color: '#1c243a', width: '15%', fontSize: '1.05rem' }}>
                                      {formData.section?.weightage ? `${formData.section.weightage}%` : '30%'}
                                    </td>
                                    <td className="align-middle font-weight-bold text-center" style={{ color: '#1c243a', width: '20%' }}>
                                      SECTION SCORE
                                    </td>
                                    <td className="align-middle font-weight-bold text-center" style={{ backgroundColor: '#fff3cd', color: '#1c243a', fontSize: '1.2rem', width: '20%' }}>
                                      {sectionScore}%
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Questions Grid */}
                          <div className="questions-container mt-2 scrollable-questions">
                            {fetchingData ? (
                              <div className="d-flex justify-content-center py-3">
                                <CustomSpinLoader loading={fetchingData} />
                              </div>
                            ) : answers && answers.length > 0 ? (
                              <div className="my-2">
                                <table className="table table-sm table-bordered shadow-sm rounded mb-0 min-table-height sticky-table-header">
                                  <thead className="thead-light">
                                    <tr>
                                      <th className="text-center align-middle" width="5%">{_t(t("#"))}</th>
                                      <th className="text-center align-middle" width="27%" style={{minWidth: "250px"}}>{_t(t("Parameter"))}</th>
                                      <th className="text-center align-middle" width="8%">{_t(t("Critical?"))}</th>
                                      <th className="text-center align-middle" width="7%">{_t(t("Max Marks"))}</th>
                                      <th className="text-center align-middle" width="13%">{_t(t("Status"))}</th>
                                      <th className="text-center align-middle" width="8%">{_t(t("Repeat Finding?"))}</th>
                                      <th className="text-center align-middle" width="9%">{_t(t("Obtained Marks"))}</th>
                                      <th className="text-center align-middle" width="15%">{_t(t("Evidence"))}</th>
                                      <th className="text-center align-middle" width="15%">{_t(t("Remarks"))}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {answers.map((ans, index) => (
                                      <tr key={index}>
                                        <td className="text-center align-middle">{index + 1}</td>
                                        <td className="align-middle">
                                          <div className="font-weight-bold mb-1">{ans.question}</div>
                                        </td>
                                        <td className="align-middle text-center bg-light font-weight-bold">
                                          {ans.critical == 1 ? "Y" : "N"}
                                        </td>
                                        <td className="align-middle text-center font-weight-bold">{ans.max_marks}</td>
                                        <td className="align-middle bg-light">
                                          <select
                                            className="form-control form-control-sm shadow-none text-center font-weight-bold"
                                            style={{
                                              backgroundColor: ans.status === 'Compliant' ? '#e8f5e9' : (ans.status === 'Non-Compliant' ? '#ffebee' : '#fff'),
                                              color: ans.status === 'Compliant' ? '#2e7d32' : (ans.status === 'Non-Compliant' ? '#c62828' : '#000')
                                            }}
                                            value={ans.status}
                                            onChange={(e) => handleAnswerChange(index, "status", e.target.value)}
                                          >
                                            <option value="Compliant">Compliant</option>
                                            <option value="Non-Compliant">Non-Compliant</option>
                                            <option value="N/A">N/A</option>
                                          </select>
                                        </td>
                                        <td className="align-middle text-center bg-light">
                                          <select
                                            className="form-control form-control-sm shadow-none text-center"
                                            value={ans.is_repeat == 1 ? 1 : 0}
                                            onChange={(e) => handleAnswerChange(index, "is_repeat", parseInt(e.target.value))}
                                          >
                                            <option value={1}>Yes</option>
                                            <option value={0}>No</option>
                                          </select>
                                        </td>
                                        <td className="align-middle bg-light">
                                          <input
                                            type="number"
                                            className="form-control form-control-sm text-center"
                                            value={ans.marks_obtained}
                                            onKeyDown={(evt) =>
                                              ["e", "E", "+", "-","ArrowUp","ArrowDown"].includes(evt.key) &&
                                              evt.preventDefault()
                                            }
                                            onWheel={(e) => e.target.blur()}
                                            onChange={(e) => handleAnswerChange(index, "marks_obtained", e.target.value ? Number(e.target.value) : "")}
                                            min="0"
                                            max={ans.max_marks || 100}
                                            required
                                          />
                                        </td>
                                        <td className="align-middle p-1 bg-light">
                                          <textarea
                                            className="form-control"
                                            rows="2"
                                            placeholder="Optional..."
                                            value={ans.evidence || ""}
                                            onChange={(e) => handleAnswerChange(index, "evidence", e.target.value)}
                                            style={{fontSize: "12px", resize: "none"}}
                                          ></textarea>
                                        </td>
                                        <td className="align-middle p-1 bg-light">
                                          <textarea
                                            className="form-control"
                                            rows="2"
                                            placeholder="Optional..."
                                            value={ans.remarks || ""}
                                            onChange={(e) => handleAnswerChange(index, "remarks", e.target.value)}
                                            style={{fontSize: "12px", resize: "none"}}
                                          ></textarea>
                                        </td>
                                      </tr>
                                    ))}
                                    <tr style={{ backgroundColor: '#243048', color: 'white' }}>
                                      <td colSpan={3} className="align-middle font-weight-bold text-white">TOTALS / SECTION SCORE</td>
                                      <td className="align-middle text-center font-weight-bold text-white">
                                        {totalMaxMarks}
                                      </td>
                                      <td colSpan={2}></td>
                                      <td className="align-middle text-center font-weight-bold text-white">
                                        {totalMarksObtained}
                                      </td>
                                      <td colSpan={2}></td>
                                    </tr>
                                    <tr>
                                      <td colSpan={3} className="align-middle">Repeat findings (this audit)</td>
                                      <td className="align-middle text-center font-weight-bold text-danger">
                                        {repeatFindingsCount}
                                      </td>
                                      <td className="align-middle text-right">Repeat Penalty</td>
                                      <td className="align-middle text-center">
                                        {repeatPenalty}%
                                      </td>
                                      <td colSpan={3}></td>
                                    </tr>
                                    <tr>
                                      <td colSpan={3} className="align-middle">Critical non-compliances found</td>
                                      <td className="align-middle text-center font-weight-bold text-danger">
                                        {criticalNonCompliancesCount}
                                      </td>
                                      <td colSpan={5}></td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-center py-4 bg-light rounded border border-light">
                                <i className="fa fa-folder-open fa-2x text-muted mb-2"></i>
                                <h6 className="text-muted small">{_t(t("Please select a section to view the audit checklist"))}</h6>
                              </div>
                            )}
                          </div>
                        
                          <div className="d-flex border-1 border-top justify-content-end pt-2 mt-2">
                            <button
                              type="button"
                              className="btn rounded hov-btn mr-3"
                              onClick={() => history.push("/dashboard/audit/rcr-list")}
                            >
                              <i className="fa fa-arrow-left" aria-hidden="true"></i> {_t(t("Back"))}
                            </button>
                            <button
                              type="submit"
                              className="btn btn-primary rounded"
                              disabled={submitting || answers.length === 0}
                            >
                              <i className="fa fa-floppy-o" aria-hidden="true"></i> {submitting ? _t(t("Saving...")) : _t(t("Save"))}
                            </button>
                          </div>
                        </div>
                      </form>
                    </>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>  
      
      <style>{`
        .custom-select, .form-control { height: calc(1.8rem + 2px); font-size: 13px; padding: 0.25rem 0.5rem; border-radius: 0.25rem; }
        .border-secondary-light { border-color: #e2e8f0; }
        .form-control:focus { border-color: #0a8d6e; box-shadow: 0 0 0 0.2rem rgba(10, 141, 110, 0.15); }
        .react-datepicker-wrapper { width: 100%; display: block; }
        .table th { border-top: none; background-color: #f8fafc; color: #475569; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.5rem; }
        .table td { vertical-align: middle; color: #334155; font-size: 13px; padding: 0.4rem; }
        .custom-switch .custom-control-label::before { width: 30px; border-radius: 30px; height: 16px; top: 0.15rem; }
        .custom-switch .custom-control-label::after { width: 12px; height: 12px; border-radius: 50%; top: calc(0.15rem + 2px); left: calc(-2.25rem + 2px); }
        .custom-control-input:checked ~ .custom-control-label::before { background-color: #0a8d6e; border-color: #0a8d6e; }
        .custom-switch .custom-control-input:checked ~ .custom-control-label::after { transform: translateX(14px); }
        .scrollable-questions { max-height: calc(100vh - 24rem); overflow: auto; border-bottom: 1px solid #e2e8f0; }
      `}</style>
    </>
  );
};

export default RcrAuditAction;
